"use client";

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { CheckCircleIcon, ShieldCheckIcon, UserIcon } from '../../components/shared/icons';

const DEFAULT_INVITE_URL = 'https://discord.gg/z8MgDnHdR';

type Props = {
  className?: string;
  title?: string;
  compact?: boolean;
};

type Gate = {
  signedIn: boolean;
  baseVerified: boolean;
  completedTabs: number;
  totalTabs: number;
  score: number;
  maxScore: number;
  eligible: boolean;
};

function getInviteUrl(): string {
  const fromEnv = process.env.NEXT_PUBLIC_DISCORD_INVITE_URL;
  const url = String(fromEnv || '').trim();
  return url || DEFAULT_INVITE_URL;
}

function getWidgetUrl(): { src: string | null; serverId: string | null } {
  const raw = String(process.env.NEXT_PUBLIC_DISCORD_SERVER_ID || '').trim();
  if (!raw) return { src: null, serverId: null };
  // Official Discord widget format:
  // https://discord.com/widget?id=SERVER_ID&theme=dark
  return {
    serverId: raw,
    src: `https://discord.com/widget?id=${encodeURIComponent(raw)}&theme=dark`,
  };
}

/**
 * Discord community panel.
 *
 * 2026-08-05 rebuild. Problems this fixes:
 *
 *  - While the eligibility check was still in flight the panel already said
 *    "Discord is locked", with "Checking your eligibility…" underneath. A
 *    signed-in, fully-eligible member was told they were locked out for as
 *    long as two round-trips took. Loading is now its own state.
 *  - A failed /api/me request rendered identically to a genuine lockout, so
 *    a network blip looked like a denial. Errors are now distinct and
 *    retryable.
 *  - The requirement was described only as prose ("finish your profile
 *    fields"), giving no way to know how much was left. The gate is
 *    `baseVerified && completedTabs === totalTabs`, and /api/me already
 *    returns both numbers - so the panel now shows the real checklist and a
 *    progress bar instead of a hardcoded score in the copy.
 *  - "Join Discord (locked)" was an inert <span>: not focusable, no
 *    explanation. It's a real disabled button with a title now.
 *  - In `compact` mode the component rendered bordered cards inside the
 *    bordered panel that hosts it, producing double borders. Compact is
 *    flat now and lets its host provide the surface.
 */
export default function DiscordCommunitySection({
  className = '',
  title = 'Join the Reqrium Community',
  compact = false,
}: Props) {
  const [gate, setGate] = useState<Gate | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');

  const check = useCallback(async () => {
    setStatus('loading');
    try {
      const statusRes = await fetch('/api/auth/status', { credentials: 'include', cache: 'no-store' });
      const auth = await statusRes.json().catch(() => ({} as { authenticated?: boolean }));

      if (!auth?.authenticated) {
        setGate({
          signedIn: false,
          baseVerified: false,
          completedTabs: 0,
          totalTabs: 0,
          score: 0,
          maxScore: 0,
          eligible: false,
        });
        setStatus('ready');
        return;
      }

      const res = await fetch('/api/me', { credentials: 'include', cache: 'no-store' });
      if (!res.ok) {
        // A 401 here genuinely means "not signed in"; anything else is a
        // fault and must not be shown as a lockout.
        if (res.status === 401) {
          setGate({
            signedIn: false, baseVerified: false, completedTabs: 0, totalTabs: 0,
            score: 0, maxScore: 0, eligible: false,
          });
          setStatus('ready');
          return;
        }
        setStatus('error');
        return;
      }

      const data = await res.json().catch(() => ({}));
      const sc = data?.socialCredit || {};
      setGate({
        signedIn: true,
        baseVerified: Boolean(data?.baseVerifiedAt),
        completedTabs: Number(sc?.completedTabs || 0),
        totalTabs: Number(sc?.totalTabs || 0),
        score: Number(sc?.total || 0),
        maxScore: Number(sc?.maxTotal || 0),
        eligible: Boolean(sc?.discordEligible),
      });
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      await check();
      if (!mounted) return;
    })();
    return () => {
      mounted = false;
    };
  }, [check]);

  const inviteUrl = getInviteUrl();
  const { src: widgetSrc } = getWidgetUrl();
  const showDiscord = status === 'ready' && gate?.eligible === true;

  const qrSrc = useMemo(
    () => `https://api.qrserver.com/v1/create-qr-code/?size=220x220&data=${encodeURIComponent(inviteUrl)}`,
    [inviteUrl]
  );

  const profileDone = gate ? gate.totalTabs > 0 && gate.completedTabs >= gate.totalTabs : false;
  const progressPct =
    gate && gate.totalTabs > 0 ? Math.round((gate.completedTabs / gate.totalTabs) * 100) : 0;

  const Heading = (
    <div className={compact ? '' : 'mb-4'}>
      <h2 className={compact ? 'text-base font-bold text-slate-100' : 'pr-section-title'}>{title}</h2>
      <p className={compact ? 'pr-hint mt-1.5' : 'pr-section-sub'}>
        Access unlocks once your wallet is verified with Base and your profile is complete.
      </p>
    </div>
  );

  // ── Loading ──────────────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <section className={className} aria-label="Discord community" aria-busy="true">
        {Heading}
        <div className="mt-4 rounded-2xl border border-white/[0.08] bg-white/[0.025] p-4">
          <div className="mb-3 h-4 w-2/5 rounded bg-white/[0.07]" />
          <div className="mb-2 h-3 w-full rounded bg-white/[0.07]" />
          <div className="h-3 w-3/5 rounded bg-white/[0.07]" />
        </div>
      </section>
    );
  }

  // ── Error ────────────────────────────────────────────────────────────
  if (status === 'error') {
    return (
      <section className={className} aria-label="Discord community">
        {Heading}
        <div className="mt-4 rounded-2xl border border-white/[0.08] bg-white/[0.025] p-4">
          <div className="text-sm font-semibold text-slate-100">Couldn&rsquo;t check your access</div>
          <p className="pr-hint mt-1.5">
            This is a connection problem, not a lockout — your eligibility hasn&rsquo;t changed.
          </p>
          <div className="pr-actions mt-4">
            <button type="button" onClick={check} className="pr-btn pr-btn-ghost pr-btn-sm">
              Try again
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={className} aria-label="Discord community">
      {Heading}

      <div className={compact ? 'flex flex-col gap-4' : 'grid grid-cols-1 gap-5 lg:grid-cols-3'}>
        <div className={compact ? '' : 'lg:col-span-2'}>
          {showDiscord ? (
            <>
              {widgetSrc ? (
                <div className="w-full overflow-hidden rounded-2xl border border-white/[0.08] bg-black/40">
                  <iframe
                    title="Reqrium Discord"
                    src={widgetSrc}
                    width="100%"
                    height={compact ? 320 : 420}
                    loading="lazy"
                    sandbox="allow-scripts allow-same-origin allow-popups"
                    referrerPolicy="no-referrer"
                    style={{ border: 0, display: 'block' }}
                  />
                </div>
              ) : (
                <div className="rounded-2xl border border-emerald-400/25 bg-emerald-400/[0.06] p-4">
                  <div className="flex items-center gap-2">
                    <CheckCircleIcon size={16} style={{ color: '#6ee7a8' }} />
                    <span className="text-sm font-semibold text-emerald-50">You&rsquo;re in</span>
                  </div>
                  <p className="pr-hint mt-1.5">Use the invite below to join the server.</p>
                </div>
              )}

              <div className="pr-actions mt-4">
                <a href={inviteUrl} target="_blank" rel="noopener noreferrer" className="pr-btn pr-btn-primary pr-btn-sm">
                  Join Discord
                </a>
              </div>
            </>
          ) : (
            /* ── Locked, with real progress ───────────────────────────── */
            <div className="rounded-2xl border border-white/[0.08] bg-white/[0.025] p-4">
              <div className="flex flex-wrap items-center gap-2">
                <span className="pr-badge pr-badge-neutral">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="11" height="11" aria-hidden="true">
                    <rect x="4" y="10" width="16" height="10" rx="2" />
                    <path d="M8 10V7a4 4 0 118 0v3" strokeLinecap="round" />
                  </svg>
                  Locked
                </span>
                <span className="text-sm font-semibold text-slate-100">Two steps to unlock</span>
              </div>

              {/* The checklist replaces the old "finish your profile fields"
                  prose - it names each requirement and marks the ones already
                  met, using the same values the server gates on. */}
              <ul className="m-0 mt-4 flex list-none flex-col gap-2 p-0">
                <GateRow
                  done={Boolean(gate?.baseVerified)}
                  icon={<ShieldCheckIcon size={14} />}
                  label="Verify your wallet with Base"
                  detail={gate?.baseVerified ? 'Done' : 'Sign a message from your Base wallet'}
                />
                <GateRow
                  done={profileDone}
                  icon={<UserIcon size={14} />}
                  label="Complete your profile"
                  detail={
                    gate && gate.totalTabs > 0
                      ? `${gate.completedTabs} of ${gate.totalTabs} sections complete`
                      : 'Fill in your profile details'
                  }
                />
              </ul>

              {gate && gate.totalTabs > 0 && (
                <div className="mt-4">
                  <div className="flex items-center justify-between text-[11px] font-semibold text-slate-400">
                    <span>Profile progress</span>
                    <span>{progressPct}%</span>
                  </div>
                  <div
                    className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-white/[0.07]"
                    role="progressbar"
                    aria-valuenow={progressPct}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label="Profile completion"
                  >
                    <div
                      className="h-full rounded-full transition-[width] duration-500"
                      style={{
                        width: `${progressPct}%`,
                        background: 'linear-gradient(90deg, #00F5FF, #7A35FF)',
                      }}
                    />
                  </div>
                  {gate.maxScore > 0 && (
                    <p className="pr-hint mt-2">
                      Social credit score {gate.score} / {gate.maxScore}
                    </p>
                  )}
                </div>
              )}

              <div className="pr-actions mt-4">
                <Link href={gate?.signedIn ? '/profile' : '/login'} className="pr-btn pr-btn-primary pr-btn-sm">
                  {gate?.signedIn ? 'Go to profile' : 'Sign in'}
                </Link>
                {gate?.signedIn && (
                  <Link href="/profile/edit" className="pr-btn pr-btn-ghost pr-btn-sm">
                    Complete profile
                  </Link>
                )}
                <button
                  type="button"
                  disabled
                  title="Unlocks after Base verification and a complete profile"
                  className="pr-btn pr-btn-ghost pr-btn-sm"
                >
                  Join Discord
                </button>
              </div>
            </div>
          )}
        </div>

        {/* ── QR ───────────────────────────────────────────────────────── */}
        <div className={compact ? '' : 'lg:col-span-1'}>
          <div className="rounded-2xl border border-white/[0.08] bg-white/[0.025] p-4">
            <div className="text-sm font-semibold text-slate-100">Scan to join</div>
            <p className="pr-hint mt-1.5">
              {showDiscord
                ? 'Scan with your camera — it opens the Discord app if installed.'
                : 'The invite QR appears here once you unlock access.'}
            </p>
            <div className="mt-4 flex items-center justify-center">
              {showDiscord ? (
                <a
                  href={inviteUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Open Discord invite"
                  className="inline-flex w-full max-w-[220px]"
                >
                  <Image
                    src={qrSrc}
                    alt="Discord invite QR code"
                    width={220}
                    height={220}
                    className="h-auto w-full rounded-xl border border-white/12"
                  />
                </a>
              ) : (
                /* Was a fixed 220x220 box, which could overrun a narrow
                   sidebar. Square via aspect-ratio, capped, never wider
                   than its column. */
                <div className="flex aspect-square w-full max-w-[220px] items-center justify-center rounded-xl border border-dashed border-white/12 bg-black/30">
                  <div className="px-4 text-center text-xs text-slate-500">Available after unlock</div>
                </div>
              )}
            </div>
            {showDiscord ? (
              <p className="mt-3 break-all text-[11px] text-slate-400">{inviteUrl}</p>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

function GateRow({
  done,
  icon,
  label,
  detail,
}: {
  done: boolean;
  icon: React.ReactNode;
  label: string;
  detail: string;
}) {
  return (
    <li className="flex items-start gap-3">
      <span
        className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
        style={{
          background: done ? 'rgba(52,211,153,0.16)' : 'rgba(255,255,255,0.06)',
          color: done ? '#6ee7a8' : '#94a3b8',
        }}
        aria-hidden="true"
      >
        {done ? <CheckCircleIcon size={14} /> : icon}
      </span>
      <span className="min-w-0">
        <span className={`block text-sm font-semibold ${done ? 'text-slate-300 line-through' : 'text-slate-100'}`}>
          {label}
        </span>
        <span className="pr-hint block">{detail}</span>
      </span>
    </li>
  );
}

// TODO: To enable the official Discord embed, set NEXT_PUBLIC_DISCORD_SERVER_ID (Discord Server ID).
